import type { Map as MapboxMap, StyleSpecification } from 'mapbox-gl';
import { attachSiteModel, type HqMapMode } from './apsHqModel';
import type { MapLightPreset } from './customModelLighting';

// Source construction anchor. The Standard presentation fit below deliberately
// spans the Standard alley-to-street gap, keeping the garden-side boundary.
export const FIFTEENTH_ORIGIN: [number, number] = [-120.99530696868896, 37.64317348136945];
const EAST_X = 0.7190050330528471;
const SOUTH_X = -0.6950048650510832;
// Mapbox converts glTF Y-up to East/South/Up itself, before this rotation.
// Retain the tiny horizontal scale correction embedded in the supplied matrix.
export const FIFTEENTH_HORIZONTAL_SCALE = Math.hypot(EAST_X, SOUTH_X);
export const FIFTEENTH_ROTATION = Math.atan2(SOUTH_X, EAST_X) * 180 / Math.PI;
export const FIFTEENTH_FIT_SCALE: [number, number, number] = [55.5 / 47, 1, 1];
// Root presentation adjustment; fixed at all zoom levels.
const FIT_OFFSET = [-1, 0];
function sourceLocalToLngLat(x: number, y: number): [number, number] {
  const east = EAST_X * x + SOUTH_X * y;
  const north = -SOUTH_X * x + EAST_X * y;
  const metresPerDegree = Math.PI * 6371008.8 / 180;
  return [FIFTEENTH_ORIGIN[0] + east / (metresPerDegree * Math.cos(FIFTEENTH_ORIGIN[1] * Math.PI / 180)),
    FIFTEENTH_ORIGIN[1] + north / metresPerDegree];
}
export const FIFTEENTH_FIT_ORIGIN = sourceLocalToLngLat(FIT_OFFSET[0], FIT_OFFSET[1]);
export function fifteenthLocalToLngLat(x: number, y: number): [number, number] {
  return sourceLocalToLngLat(x * FIFTEENTH_FIT_SCALE[0] + FIT_OFFSET[0], y * FIFTEENTH_FIT_SCALE[1] + FIT_OFFSET[1]);
}
// Presentation street-edge envelope from the approved grounds, including parking
// and the 15th Street sidewalk. This suppresses only basemap objects, never the asset.
export const FIFTEENTH_BOUNDARY = [[-1, -18], [46, -18], [46, 27], [-1, 27], [-1, -18]].map(([x, y]) => fifteenthLocalToLngLat(x, y));
export function fifteenthModelStyle(uri: string): StyleSpecification {
  return {
    version: 8,
    sources: {
      '909-15th-st-asset': { type: 'model', models: {
        '909-15th-st': { uri, position: FIFTEENTH_FIT_ORIGIN },
      } },
      '909-15th-st-boundary': { type: 'geojson', data: {
        type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [FIFTEENTH_BOUNDARY] },
      } },
    },
    layers: [
      { id: '909-15th-st-replacement', type: 'clip', source: '909-15th-st-boundary',
        layout: { 'clip-layer-types': ['model', 'symbol'], 'clip-layer-scope': ['basemap'] } },
      { id: '909-15th-st-model', type: 'model', source: '909-15th-st-asset', slot: 'middle',
        paint: { 'model-rotation': [0, 0, FIFTEENTH_ROTATION], 'model-scale': [FIFTEENTH_HORIZONTAL_SCALE * FIFTEENTH_FIT_SCALE[0], FIFTEENTH_HORIZONTAL_SCALE * FIFTEENTH_FIT_SCALE[1], 1],
          'model-translation': [0, 0, 0.03], 'model-color-mix-intensity': 0, 'model-opacity': 1,
          'model-cast-shadows': true, 'model-receive-shadows': true, 'model-emissive-strength': 0,
          'model-cutoff-fade-range': 0, 'model-line-cutout-mode': 'disabled' },
      },
    ],
  };
}
export function attachFifteenthStreetModel(map: MapboxMap, mode: HqMapMode,
  onError: (message: string | null) => void, lightPreset: MapLightPreset) {
  const uri = new URL(`${import.meta.env.BASE_URL}models/909-15th-st/mapbox.glb?v=58f868fc1830`, window.location.href).href;
  return attachSiteModel(map, mode, onError, lightPreset, {
    name: '909 Fifteenth Street', asset: '909-15th-st-asset', model: '909-15th-st-model',
    clip: '909-15th-st-replacement', style: fifteenthModelStyle(uri), errorPattern: /909-15th-st/i,
  });
}
